import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageShell } from '@/components/ds';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { AgencyLogo } from '@/components/agency/AgencyLogo';
import { agencyAPI } from '@/lib/api';
import { isKRRole, buildAgencyTree, getErrorMessage, formatPhoneInput } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  ChevronRight,
  ChevronDown,
  FolderTree,
  Edit,
  Loader2,
  Trash2,
  Search,
  Filter,
  X,
  ChevronsUpDown,
  Users
} from 'lucide-react';

export default function AgenciesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [agencyTree, setAgencyTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [agencyToDelete, setAgencyToDelete] = useState(null);
  const [editingAgency, setEditingAgency] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [agencySearch, setAgencySearch] = useState('');
  const [agencyStatusFilter, setAgencyStatusFilter] = useState('all');
  const [agentCounts, setAgentCounts] = useState({});
  const [principals, setPrincipals] = useState({});
  const [formData, setFormData] = useState({
    agency_name: '',
    parent_agency_id: '',
    primary_contact_name: '',
    email: '',
    phone: '',
    notes_internal: '',
    status: 'active'
  });

  const isKR = isKRRole(user);

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      const [response, countsRes, principalsRes] = await Promise.all([
        agencyAPI.list(),
        agencyAPI.getAgentCounts(),
        agencyAPI.getPrincipals().catch(() => ({ data: {} })),
      ]);
      setAgencies(response.data);
      setAgencyTree(buildAgencyTree(response.data));
      setAgentCounts(countsRes.data);
      setPrincipals(principalsRes.data || {});
      const rootIds = response.data.filter(a => !a.parent_agency_id).map(a => a.agency_id);
      setExpandedNodes(new Set(rootIds));
    } catch (error) {
      toast.error('Failed to load agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAgency) {
        await agencyAPI.update(editingAgency.agency_id, formData);
        toast.success('Agency updated successfully');
      } else {
        await agencyAPI.create({
          ...formData,
          parent_agency_id: formData.parent_agency_id || null
        });
        toast.success('Agency created successfully');
      }
      setShowDialog(false);
      resetForm();
      loadAgencies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const resetForm = () => {
    setFormData({
      agency_name: '',
      parent_agency_id: '',
      primary_contact_name: '',
      email: '',
      phone: '',
      notes_internal: '',
      status: 'active'
    });
    setEditingAgency(null);
  };

  const openAddDialog = (parentId = '') => {
    setEditingAgency(null);
    setFormData({
      agency_name: '',
      parent_agency_id: parentId,
      primary_contact_name: '',
      email: '',
      phone: '',
      notes_internal: '',
      status: 'active'
    });
    setShowDialog(true);
  };

  const openEditDialog = (agency) => {
    setEditingAgency(agency);
    setFormData({
      agency_name: agency.agency_name,
      parent_agency_id: agency.parent_agency_id || '',
      primary_contact_name: agency.primary_contact_name || '',
      email: agency.email || '',
      phone: agency.phone || '',
      notes_internal: agency.notes_internal || '',
      status: agency.status
    });
    setShowDialog(true);
  };

  const handleDelete = async () => {
    if (!agencyToDelete) return;
    try {
      await agencyAPI.delete(agencyToDelete.agency_id);
      toast.success('Agency deleted successfully');
      setShowDeleteDialog(false);
      setAgencyToDelete(null);
      loadAgencies();
    } catch (error) {
      const msg = error.response?.data?.detail || getErrorMessage(error, 'Failed to delete agency');
      toast.error(msg);
      setShowDeleteDialog(false);
    }
  };

  const toggleNode = (agencyId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(agencyId)) {
        next.delete(agencyId);
      } else {
        next.add(agencyId);
      }
      return next;
    });
  };

  const expandAll = () => setExpandedNodes(new Set(agencies.map(a => a.agency_id)));
  const collapseAll = () => setExpandedNodes(new Set());

  // Flatten tree for rendering (non-recursive approach) with search & filter
  const getFlattenedTree = useCallback(() => {
    const q = agencySearch.toLowerCase().trim();
    const statusFilter = agencyStatusFilter;
    
    // If no search or filter, render normally
    if (!q && statusFilter === 'all') {
      const result = [];
      const stack = agencyTree.map(node => ({ node, depth: 0 })).reverse();
      while (stack.length > 0) {
        const { node, depth } = stack.pop();
        result.push({ ...node, displayDepth: depth });
        if (node.children && node.children.length > 0 && expandedNodes.has(node.agency_id)) {
          for (let i = node.children.length - 1; i >= 0; i--) {
            stack.push({ node: node.children[i], depth: depth + 1 });
          }
        }
      }
      return result;
    }
    
    // With search/filter: show flat list of matches
    return agencies
      .filter(a => {
        if (statusFilter !== 'all' && a.status !== statusFilter) return false;
        if (q && !a.agency_name.toLowerCase().includes(q) && 
            !(a.primary_contact_name || '').toLowerCase().includes(q) &&
            !(a.email || '').toLowerCase().includes(q) &&
            !(a.agency_id || '').toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => a.agency_name.localeCompare(b.agency_name))
      .map(a => ({ ...a, displayDepth: 0, children: [] }));
  }, [agencyTree, agencies, expandedNodes, agencySearch, agencyStatusFilter]);

  // Get parent agency names for display
  const getParentName = useCallback((parentId) => {
    if (!parentId) return null;
    const parent = agencies.find(a => a.agency_id === parentId);
    return parent?.agency_name || null;
  }, [agencies]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const flattenedTree = getFlattenedTree();
  const isSearching = agencySearch.trim() || agencyStatusFilter !== 'all';

  return (
    <PageShell
      title="Agency Tree"
      description="The full KR Enterprise Group organization hierarchy. Expand any branch to explore downline agencies, their principals and status."
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Business' },
        { label: 'Agencies' },
      ]}
      actions={
        isKR && (
          <Button onClick={() => openAddDialog()} data-testid="add-agency-btn">
            <Plus size={16} className="mr-2" />
            Add Agency
          </Button>
        )
      }
      testId="agencies-page"
    >
      <div className="page-content">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <FolderTree size={20} className="text-slate-400" />
                Organization Hierarchy
              </CardTitle>
              <div className="flex items-center gap-2">
                {!isSearching && agencies.length > 0 && (
                  <Button variant="ghost" size="sm" className="h-8 text-xs gap-1"
                    onClick={() => expandedNodes.size === agencies.length ? collapseAll() : expandAll()}
                    data-testid="toggle-all-btn">
                    <ChevronsUpDown size={14} />
                    {expandedNodes.size === agencies.length ? 'Collapse All' : 'Expand All'}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search & Filter Toolbar */}
            <div className="space-y-3 mb-4" data-testid="agency-toolbar">
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={agencySearch}
                    onChange={(e) => setAgencySearch(e.target.value)}
                    placeholder="Search by agency name, contact, or email..."
                    className="pl-9 h-9 text-sm"
                    data-testid="agency-search-input"
                  />
                  {agencySearch && (
                    <button onClick={() => setAgencySearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X size={14} />
                    </button>
                  )}
                </div>
                <div className="min-w-[150px]">
                  <Select value={agencyStatusFilter} onValueChange={setAgencyStatusFilter}>
                    <SelectTrigger className="h-9 text-sm" data-testid="agency-status-filter">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="hidden sm:flex items-center text-xs text-slate-500 px-2 shrink-0">
                  {flattenedTree.length} result{flattenedTree.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            {flattenedTree.length === 0 ? (
              <EmptyState
                icon={isSearching ? Search : Building2}
                title={isSearching ? "No agencies match" : "No agencies found"}
                description={isSearching ? "Try adjusting your search or filters." : "Get started by creating your first agency."}
                action={
                  !isSearching && isKR ? (
                    <Button onClick={() => openAddDialog()}>
                      <Plus size={16} className="mr-2" />
                      Add Agency
                    </Button>
                  ) : null
                }
              />
            ) : (
              <div className="space-y-0">
                {flattenedTree.map((node, idx) => {
                  const hasChildren = node.children && node.children.length > 0;
                  const isExpanded = expandedNodes.has(node.agency_id);
                  const canAddChild = isKR && node.depth < 5;
                  const depth = node.displayDepth;
                  const agentCount = agentCounts[node.agency_id] || 0;
                  
                  return (
                    <div 
                      key={node.agency_id}
                      className="relative flex items-center p-3 rounded-lg hover:bg-slate-50 transition-colors group"
                      style={{ paddingLeft: `${depth * 28 + 12}px` }}
                      data-testid={`tree-node-${node.agency_id}`}
                    >
                      {/* Connecting line */}
                      {depth > 0 && !isSearching && (
                        <div className="absolute border-l-2 border-slate-200"
                          style={{ left: `${(depth - 1) * 28 + 24}px`, top: 0, height: '50%' }} />
                      )}
                      {depth > 0 && !isSearching && (
                        <div className="absolute border-b-2 border-slate-200"
                          style={{ left: `${(depth - 1) * 28 + 24}px`, top: '50%', width: '16px' }} />
                      )}

                      <button
                        onClick={() => toggleNode(node.agency_id)}
                        className={`w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200 mr-2 ${(!hasChildren || isSearching) ? 'invisible' : ''}`}
                        data-testid={`toggle-${node.agency_id}`}
                      >
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                      
                      <div className={`mr-3 shrink-0`}>
                        <AgencyLogo
                          agency={node}
                          size={depth === 0 ? 'w-9 h-9' : 'w-9 h-9'}
                          iconSize={18}
                          testId={`agency-logo-${node.agency_id}`}
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/agencies/${node.agency_id}`)}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 truncate hover:text-sky-600 transition-colors">{node.agency_name}</span>
                          <StatusBadge status={node.status} />
                          {agentCount > 0 && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-normal gap-0.5">
                              <Users size={10} /> {agentCount}
                            </Badge>
                          )}
                          {principals[node.agency_id] && (
                            <div
                              className="flex items-center gap-1.5 pl-2 ml-1 border-l border-slate-200"
                              title={`Principal: ${principals[node.agency_id].name}`}
                              data-testid={`tree-principal-${node.agency_id}`}
                            >
                              <AgentAvatar
                                agent={{
                                  npn: principals[node.agency_id].npn,
                                  first_name: (principals[node.agency_id].name || '').split(' ')[0] || '',
                                  last_name: (principals[node.agency_id].name || '').split(' ').slice(1).join(' ') || '',
                                  photo_url: principals[node.agency_id].photo_url,
                                }}
                                size="w-6 h-6"
                                rounded="full"
                                textSize="text-[9px]"
                                testId={`tree-principal-avatar-${node.agency_id}`}
                              />
                              <span className="text-[11px] text-slate-500 truncate max-w-[140px]">
                                {principals[node.agency_id].name}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {isSearching && getParentName(node.parent_agency_id) ? (
                            <>Parent: {getParentName(node.parent_agency_id)} &bull; </>
                          ) : null}
                          Depth: {node.depth} {!isSearching && <>&bull; {node.children?.length || 0} sub-agencies</>}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {canAddChild && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openAddDialog(node.agency_id)}
                            data-testid={`add-child-${node.agency_id}`}
                          >
                            <Plus size={14} className="mr-1" />
                            Add Sub
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditDialog(node)}
                          data-testid={`edit-${node.agency_id}`}
                        >
                          <Edit size={14} />
                        </Button>
                        {isKR && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              setAgencyToDelete(node);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-${node.agency_id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingAgency ? 'Edit Agency' : 'Add New Agency'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="agency_name">Agency Name *</Label>
                <Input
                  id="agency_name"
                  value={formData.agency_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, agency_name: e.target.value }))}
                  placeholder="Enter agency name"
                  required
                  data-testid="agency-name-input"
                />
              </div>
              
              {!editingAgency && (
                <div className="space-y-2">
                  <Label htmlFor="parent_agency_id">Parent Agency</Label>
                  <Select
                    value={formData.parent_agency_id || '__none__'}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, parent_agency_id: value === '__none__' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="parent-agency-select">
                      <SelectValue placeholder="Select parent (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None (Root Agency)</SelectItem>
                      {agencies.filter(a => a.depth < 5).map(agency => (
                        <SelectItem key={agency.agency_id} value={agency.agency_id}>
                          {'—'.repeat(agency.depth)} {agency.agency_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_contact_name">Contact Name</Label>
                  <Input
                    id="primary_contact_name"
                    value={formData.primary_contact_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, primary_contact_name: e.target.value }))}
                    placeholder="Primary contact"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formatPhoneInput(formData.phone)}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone number"
                    data-testid="phone-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="agency@example.com"
                  data-testid="email-input"
                />
              </div>
              
              {editingAgency && (
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="notes_internal">Internal Notes</Label>
                <Textarea
                  id="notes_internal"
                  value={formData.notes_internal}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes_internal: e.target.value }))}
                  placeholder="Internal notes (not visible to agency)"
                  rows={3}
                  data-testid="notes-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-agency-btn">
                {editingAgency ? 'Save Changes' : 'Create Agency'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Agency</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Are you sure you want to delete <strong>{agencyToDelete?.agency_name}</strong>?</p>
                <p className="mt-2 text-red-600 font-medium">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-agency">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-agency">
              Delete Agency
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
